import { useState, useEffect } from 'react';
import { Settings, Save, RefreshCw, AlertCircle, CheckCircle, XCircle, Trash2, Send } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { syslogApi, SyslogConfigCreate, SyslogTestResponse } from '@/lib/api/syslog';
import toast from 'react-hot-toast';

const protocolOptions = [
  { value: 'udp', label: 'UDP' },
  { value: 'tcp', label: 'TCP' },
];

const logLevelOptions = [
  { value: 'DEBUG', label: 'Debug' },
  { value: 'INFO', label: 'Info' },
  { value: 'WARNING', label: 'Warning' },
  { value: 'ERROR', label: 'Error' },
  { value: 'CRITICAL', label: 'Critical' },
];

const facilityOptions = Array.from({ length: 8 }, (_, i) => ({
  value: `local${i}`,
  label: `local${i}`,
}));

interface FormData {
  host: string;
  port: number;
  protocol: string;
  log_level: string;
  facility: string;
  app_name: string;
  is_enabled: boolean;
}

const defaultFormData: FormData = {
  host: '',
  port: 514,
  protocol: 'udp',
  log_level: 'INFO',
  facility: 'local0',
  app_name: 'netapp-dashboard',
  is_enabled: false,
};

function SyslogSettingsPage() {
  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<SyslogTestResponse | null>(null);
  const [configExists, setConfigExists] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const config = await syslogApi.getConfig();
      setFormData({
        host: config.host,
        port: config.port,
        protocol: config.protocol,
        log_level: config.log_level,
        facility: config.facility,
        app_name: config.app_name,
        is_enabled: config.is_enabled,
      });
      setConfigExists(true);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setFormData(defaultFormData);
        setConfigExists(false);
      } else {
        setError('Failed to load syslog configuration');
        console.error('Failed to fetch syslog config:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async () => {
    try {
      if (!configExists) return;
      const updated = await syslogApi.toggleConfig();
      setFormData(prev => ({ ...prev, is_enabled: updated.is_enabled }));
      toast.success(`Syslog forwarding ${updated.is_enabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      console.error('Failed to toggle syslog config:', err);
      toast.error('Failed to toggle syslog forwarding');
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      const result = await syslogApi.testConnection({
        host: formData.host,
        port: formData.port,
        protocol: formData.protocol,
      });
      setTestResult(result);
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err?.response?.data?.detail || 'Connection test failed',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!formData.host.trim()) {
      toast.error('Host is required');
      return;
    }
    if (formData.port < 1 || formData.port > 65535) {
      toast.error('Port must be between 1 and 65535');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const payload: SyslogConfigCreate = {
        host: formData.host,
        port: formData.port,
        protocol: formData.protocol,
        log_level: formData.log_level,
        facility: formData.facility,
        app_name: formData.app_name,
        is_enabled: formData.is_enabled,
      };
      await syslogApi.saveConfig(payload);
      setConfigExists(true);
      toast.success('Syslog configuration saved successfully');
    } catch (err: any) {
      const message = err?.response?.data?.detail || 'Failed to save syslog configuration';
      toast.error(message);
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete the syslog configuration?')) {
      return;
    }

    try {
      await syslogApi.deleteConfig();
      setFormData(defaultFormData);
      setConfigExists(false);
      setTestResult(null);
      toast.success('Syslog configuration deleted');
    } catch (err) {
      console.error('Failed to delete syslog config:', err);
      toast.error('Failed to delete syslog configuration');
    }
  };

  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500 dark:text-gray-400">Loading syslog settings...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: 'Settings', href: '/settings', icon: <Settings className="w-4 h-4" /> },
            { label: 'Syslog', icon: <Settings className="w-4 h-4" /> },
          ]}
        />

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Syslog Settings
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Configure syslog forwarding for centralized log management
            </p>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" />
              <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
            </div>
          </div>
        )}

        {/* Syslog Configuration Card */}
        <div className="bg-white dark:bg-[#1a1f5c]/50 rounded-xl border border-gray-200 dark:border-[#5869ff]/20 p-6">
          {/* Card Header with Toggle */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Settings className="w-6 h-6 text-[#5869ff]" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Syslog Forwarding Configuration
              </h2>
            </div>

            {/* Enable/Disable Toggle */}
            <button
              onClick={handleToggle}
              disabled={!configExists}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                !configExists
                  ? 'bg-gray-200 dark:bg-gray-700 cursor-not-allowed opacity-50'
                  : formData.is_enabled
                    ? 'bg-green-500'
                    : 'bg-gray-300 dark:bg-gray-600'
              }`}
              title={configExists ? (formData.is_enabled ? 'Disable' : 'Enable') : 'Save configuration first'}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                  formData.is_enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Host */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Host <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.host}
                onChange={(e) => updateField('host', e.target.value)}
                placeholder="e.g. syslog.example.com"
                className="w-full px-3 py-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:outline-none focus:border-[#5869ff] placeholder-gray-400 dark:placeholder-gray-500"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Hostname or IP address of the syslog server
              </p>
            </div>

            {/* Port */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Port
              </label>
              <input
                type="number"
                value={formData.port}
                onChange={(e) => updateField('port', parseInt(e.target.value) || 514)}
                min={1}
                max={65535}
                className="w-full px-3 py-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:outline-none focus:border-[#5869ff]"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Port number (1-65535, default: 514)
              </p>
            </div>

            {/* Protocol */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Protocol
              </label>
              <select
                value={formData.protocol}
                onChange={(e) => updateField('protocol', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:outline-none focus:border-[#5869ff]"
              >
                {protocolOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Log Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Log Level
              </label>
              <select
                value={formData.log_level}
                onChange={(e) => updateField('log_level', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:outline-none focus:border-[#5869ff]"
              >
                {logLevelOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Minimum log level to forward to syslog
              </p>
            </div>

            {/* Facility */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Facility
              </label>
              <select
                value={formData.facility}
                onChange={(e) => updateField('facility', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:outline-none focus:border-[#5869ff]"
              >
                {facilityOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Syslog facility code for message categorization
              </p>
            </div>

            {/* App Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                App Name
              </label>
              <input
                type="text"
                value={formData.app_name}
                onChange={(e) => updateField('app_name', e.target.value)}
                placeholder="netapp-dashboard"
                className="w-full px-3 py-2 border border-gray-300 dark:border-[#5869ff]/30 rounded-lg bg-white dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white focus:outline-none focus:border-[#5869ff] placeholder-gray-400 dark:placeholder-gray-500"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Application identifier in syslog messages
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 mt-6">
            <button
              onClick={handleTestConnection}
              disabled={testing || !formData.host.trim()}
              className="px-4 py-2 border border-gray-300 dark:border-[#5869ff]/30 text-gray-700 dark:text-gray-300 rounded-lg flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-[#1a1f5c]/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {testing ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Test Connection
            </button>

            <button
              onClick={handleSave}
              disabled={saving || !formData.host.trim()}
              className="px-4 py-2 bg-gradient-to-r from-[#5869ff] to-[#000742] text-white rounded-lg flex items-center gap-2 hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save
            </button>

            {configExists && (
              <button
                onClick={handleDelete}
                className="px-4 py-2 border border-red-300 dark:border-red-500/30 text-red-600 dark:text-red-400 rounded-lg flex items-center gap-2 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            )}
          </div>

          {/* Test Result */}
          {testResult && (
            <div
              className={`mt-4 p-4 rounded-lg border ${
                testResult.success
                  ? 'bg-green-50 dark:bg-green-500/10 border-green-200 dark:border-green-500/30'
                  : 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/30'
              }`}
            >
              <div className="flex items-start gap-3">
                {testResult.success ? (
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" />
                )}
                <p
                  className={`text-sm ${
                    testResult.success
                      ? 'text-green-800 dark:text-green-300'
                      : 'text-red-800 dark:text-red-300'
                  }`}
                >
                  {testResult.message}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

export { SyslogSettingsPage };
